import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, MessageCircle, Music, Podcast, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { getMediaUrl } from '../utils/mediaUrl';

const MyComments = () => {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    try {
      const response = await axios.get('/api/me/comments-all');
      setComments(response.data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Erreur lors du chargement des commentaires');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
      return;
    }

    try {
      await axios.delete(`/api/comments/${commentId}`);
      toast.success('Commentaire supprimé');
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-md border-b border-white/10 p-4">
        <div className="flex items-center space-x-4 max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Mes commentaires</h1>
            <p className="text-sm text-gray-400">{comments.length} commentaire{comments.length > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 pb-24 max-w-4xl">
        {loading ? (
          <div className="text-center text-gray-400 py-10">
            Chargement...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-20">
            <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Vous n'avez pas encore laissé de commentaires</p>
            <p className="text-gray-500 text-sm mt-2">Vos commentaires sur les Gim Toli, Podcasts et Courts-métrages apparaîtront ici</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-comments-list">
            {comments.map(comment => {
              const typeLabel = {
                gim_toli: 'Gim Toli', podcast: 'Podcast', short_film: 'Court-métrage', studio_story: 'Histoire audio', photo: 'Photo',
              }[comment.type] || comment.type;
              const typeColor = {
                gim_toli: 'bg-fuchsia-500/20 text-fuchsia-300',
                podcast: 'bg-violet-500/20 text-violet-300',
                short_film: 'bg-cyan-500/20 text-cyan-300',
                studio_story: 'bg-amber-500/20 text-amber-300',
                photo: 'bg-pink-500/20 text-pink-300',
              }[comment.type] || 'bg-white/10 text-white/80';
              return (
                <div key={comment.id} className="bg-white/5 rounded-2xl border border-white/10 p-4 hover:border-violet-500/30 transition-all" data-testid={`my-comment-${comment.id}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${typeColor}`}>{typeLabel}</span>
                    {comment.context_title && <span className="text-xs text-gray-400 truncate">sur « {comment.context_title} »</span>}
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 mb-3">
                    <p className="text-gray-200 whitespace-pre-wrap break-words">{comment.text}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="flex items-center space-x-1 text-red-400 hover:text-red-300 text-sm transition-colors"
                      data-testid={`my-comment-delete-${comment.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Navigation />
    </div>
  );
};

export default MyComments;
